"use client";

import React, { useRef, useCallback, useState } from 'react';
import styles from './_gc-step-assess-treatment-progress.module.scss';
import GcStepShell from '../../components/gc-step-shell';
import { FormInputCalendar } from '@/components/form';
import Button from '@/components/button';
import { GC_CALC_POPULATION, GC_CALC_STATUS } from '@/data/gc-calculator-data';


// Shared position fn — 8px above trigger, horizontally centered in the calc column
function gcTooltipPositionFn(target, tooltipEl) {
  const col = target.closest('[data-gc-calc-col]');
  const targetRect = target.getBoundingClientRect();
  const tipRect = tooltipEl.getBoundingClientRect();
  const colRect = col ? col.getBoundingClientRect() : targetRect;
  return {
    top: targetRect.top + window.scrollY - 8 - tipRect.height,
    left: colRect.left + window.scrollX + (colRect.width - tipRect.width) / 2,
  };
}

const SUPPORTIVE_CARE_TOOLTIP = {
  openClass: 'gc-calc-tooltip',
  maxWidth: '384px',
  positionFn: gcTooltipPositionFn,
  content: (
    <p>
      Supportive care may include prescribing NSAIDs,
      recommending light activity (e.g., gentle stretching),
      and/or suggesting relaxing in a warm bath.
    </p>
  ),
};

const ADULT_TREATMENT_PROGRESS_ITEMS = [
  {
    label: 'Potential adrenal insufficiency',
    recommendation: 'For adrenal insufficiency, increase daily GC dose by ~2.5 mg HCe and STOP reducing GC dose.',
  },
  {
    label: 'Severe (unable to continue usual activities)',
    recommendation: (
      <>
        Provide supportive care
        <Button
          type="tooltip"
          tooltipData={SUPPORTIVE_CARE_TOOLTIP}
          customClass={styles.tooltip_btn}
          aria-label="More information about supportive care"
        >
          &#9432;
        </Button> and reassurance and increase GC halfway to previous tolerated dose. Wait ~1-2 weeks and
        assess whether symptoms have improved. If symptoms improve or resolve, continue GC dose
        reduction to physiologic range if A4 &lt; goal. If symptoms persist, consider other etiologies,
        such as adrenal insufficiency.
      </>
    ),
  },
  {
    label: 'Mild-moderate (able to continue usual activities)',
    recommendation: (
      <>
        Provide supportive care
        <Button
          type="tooltip"
          tooltipData={SUPPORTIVE_CARE_TOOLTIP}
          customClass={styles.tooltip_btn}
          aria-label="More information about supportive care"
        >
          &#9432;
        </Button> and reassurance, wait 1-2 weeks, assess whether symptoms have improved,
        and continue GC dose reduction to physiologic range as tolerated if A4 &lt; goal.
      </>
    ),
  },
  {
    label: 'No symptoms',
    recommendation: 'Patient is tolerating GC reduction. Continue dose reduction to physiologic range if A4 remains within target range.',
  },
];

/**
 * @description GC OPTIMIZE™ Calculator — Assess Treatment Progress
 * Adult: static recommendation screen shown for ≤50 HCe with prior down-titration.
 * Pediatric: date entry screen used on the pediatric path.
 */
function GcStepAssessTreatmentProgress({ state, dispatch, onBack, onNext, isReview, stepNumber }) {
  const isAdult = state.population === GC_CALC_POPULATION.ADULT;

  const labDateRef = useRef(state.formValues.assessLabDrawDate ?? '');
  const labCalRef = useRef(null);

  const [isNextDisabled, setIsNextDisabled] = useState(() => !labDateRef.current);

  const validateDate = useCallback(() => {
    setIsNextDisabled(!labDateRef.current);
  }, []);

  const handleAdultSave = useCallback(() => {
    onNext();
  }, [onNext]);

  const handlePediatricSave = useCallback(() => {
    dispatch({ type: 'SET_FIELD', field: 'assessLabDrawDate', value: labDateRef.current });
    onNext();
  }, [dispatch, onNext]);

  if (isAdult) {
    return (
      <GcStepShell
        title="Assess Treatment Progress"
        showBack={false}
        onBack={onBack}
        onNext={handleAdultSave}
        isReview={isReview}
        nextLabel="Save &amp; continue"
        nextVariant={['solid', 'solid--eggplant']}
        stepNumber={stepNumber}
      >
        <div className={styles.adult_content}>
          <p className={styles.adult_intro}>
            Common symptoms of GC withdrawal include general malaise, fatigue, nausea,
            arthralgia, sleep disturbances, and mood changes. The risk of GC withdrawal
            symptoms is greater with higher-potency GCs (eg, dexamethasone). GC withdrawal
            symptoms can overlap with those for adrenal insufficiency and include headache,
            fatigue, body aches, joint pain, and nausea. Discuss with your patient and,
            if they report GC withdrawal symptoms, determine the severity of them. The
            most common adverse reactions in adults taking CRENESSITY were fatigue,
            headache, dizziness, arthralgia, back pain, decreased appetite, and myalgia.
          </p>

          <ul className={styles.adult_bullet_list}>
            {ADULT_TREATMENT_PROGRESS_ITEMS.map((item) => (
              <li key={item.label} className={styles.adult_bullet_item}>
                {item.label}
                <ul className={styles.adult_recommendation_list}>
                  <li className={styles.adult_recommendation}>
                    <span className={styles.recommendation_label}>Recommendation:</span>{' '}
                    {item.recommendation}
                  </li>
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </GcStepShell>
    );
  }

  return (
    <GcStepShell
      title="Assess Treatment Progress"
      showBack={false}
      onBack={onBack}
      onNext={handlePediatricSave}
      isNextDisabled={isNextDisabled}
      isReview={isReview}
      nextLabel="Save &amp; continue"
      nextVariant={['solid', 'solid--eggplant']}
    >
      <div className={styles.assess_content}>
        {
          !(state.status === GC_CALC_STATUS.CONTINUING && state.formValues.a4Assessment === 'below-goal') ? (
            <p className={styles.assess_intro}>
              Evaluate fludrocortisone dose and discuss stress dosing. If previous GC dose
              reduction was not tolerated, assess/treat for GC withdrawal vs adrenal
              insufficiency and reassess in ~2 weeks.
            </p>
          ) : (
            <p className={styles.assess_intro}>
              Evaluate fludrocortisone dose and discuss stress dosing. Monitor for signs
              of GC withdrawal and adrenal insufficiency.
            </p>
          )
        }

        <div className={styles.date_field_group}>
          <FormInputCalendar
            ref={labCalRef}
            name="assess-lab-draw-clinical-date"
            label="Lab draw/clinical assessment date*"
            yearDropdownYearsBack={5}
            yearDropdownYearsAhead={1}
            defaultValue={state.formValues.assessLabDrawDate ?? ''}
            onBlur={(e) => {
              labDateRef.current = e.target.value;
              validateDate();
            }}
            onChange={(e) => {
              labDateRef.current = e.target.value;
              validateDate();
            }}
            customClass={styles.date_input}
          />
        </div>

        <p className={styles.assess_footnote}>
          <em className="hang">*</em>
          <em>
            May include clinical and laboratory assessments such as symptoms, blood pressure,
            ACTH, 17-OHP, A4, testosterone, renin, and electrolytes.
          </em>
        </p>
      </div>
    </GcStepShell>
  );
}

export default GcStepAssessTreatmentProgress;
